import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import WindowedSelect from 'react-windowed-select';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import LeaseApiService from '../../services/apiService';
import { FaSave, FaAngleLeft, FaTrash } from 'react-icons/fa';
import { RiRefreshLine } from 'react-icons/ri';
import api from '../../services/leaseGeneralInvoiceReceiptApi';
import AllocationGrid from './AllocationGrid';
import {
    clearAllocations, fmt3, mergeKeepingTicks, num, round3, setRowAmount, summary, toggleRow,
} from './allocationHelpers';
import { isChequeOrPdc, isSetOff, MAX, validateReceipt } from './validation';
import {
    emptyToNull, getFinancialYearRange, getSelectedFinancialYear, parseApiDate, selectStyles, toApiDate, toOptions,
} from './formUtils';

const DROPDOWN_TYPES = ['Customers', 'PaymentTypes', 'AdditionalAccounts', 'FinancialYears', 'CostCentre1', 'CostCentre2', 'CostCentre3', 'CostCentre4', 'Currencies', 'ReceiptPropertyNos'];
const DEFAULT_CURRENCY = 'BHD';
const LOCK_NOTE = 'Cheque already cleared or Credit Note / Adjustment: payment type and account are locked.';
const COST_CENTRES = [1, 2, 3, 4];

// Dropdowns are loaded once and cached for the session.
let dropdownCache = null;
const DEFAULT_PAYMENT_TYPE = 'Bank Transfer';

const initialValues = () => ({
    customer: null,
    paymentType: '',
    debitAccount: null,
    setOffFy: getSelectedFinancialYear(),
    setOffVoucher: null,
    amount: '',
    currency: DEFAULT_CURRENCY,
    conversionRate: 1,
    chequeNo: '',
    chequeDate: null,
    drawnBank: '',
    voucherDate: new Date(),
    refNo: '',
    narration: '',
    cc1: '', cc2: '', cc3: '', cc4: '',
    additionalAccount: null,
    additionalAmount: '',
    unitNo: null,
    property: null,
    leaseNo: null,
});

const Field = ({ label, error, children, className = '' }) => (
    <div className={className}>
        <div className="d-flex align-items-start">
            <label className="form-label pt-1 start" style={{ minWidth: 130 }}>{label}</label>
            <div className="w-100">{children}</div>
        </div>
        {error && <div className="text-danger small" style={{ marginLeft: 130 }}>{error}</div>}
    </div>
);

const Section = ({ title, children }) => (
    <div className="cost-center-section mt-3">
        <div className="d-flex slide-header"><div className="param-label pt-1">{title}</div></div>
        <div className="border-box2 p-3">{children}</div>
    </div>
);

const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '18px' };

/**
 * @param {{ mode: 'create'|'edit'|'view', rdNo?: number|string, onClose: (saved: boolean) => void }} props
 */
const LeaseGeneralInvoiceReceiptForm = ({ mode, rdNo, onClose }) => {
    const isCreate = mode === 'create';
    const isEdit = mode === 'edit';

    const [loadState, setLoadState] = useState('loading'); // loading | ready | notfound | error
    const [loadMessage, setLoadMessage] = useState('');
    const [dropdowns, setDropdowns] = useState({});
    const [header, setHeader] = useState(null);
    const [values, setValues] = useState(initialValues());
    const [rows, setRows] = useState([]);
    const [customerDetail, setCustomerDetail] = useState(null);
    const [debitAccounts, setDebitAccounts] = useState([]);
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState({ customer: false, invoices: false, accounts: false, vouchers: false });
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState('');
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [leaseNo, setLeaseNo] = useState([]);
    const [property, setProperty] = useState([]);
    const [unitList, setUnitList] = useState([]);
    const [confirm, setConfirm] = useState(null); // 'save' | 'discard' | 'remove'
    const invoiceRequest = useRef(0);

    const apiService = new LeaseApiService();

    const setLoadingFlag = (key, value) => setLoading((prev) => ({ ...prev, [key]: value }));
    const setValue = (patch) => {
        setValues((prev) => ({ ...prev, ...patch }));
        setDirty(true);
    };

    const setOff = isSetOff(values.paymentType);
    const showCheque = isChequeOrPdc(values.paymentType);
    const locked = mode === 'view' || (isEdit && header && !header.canEdit);
    const paymentLocked = locked || (isEdit && header?.lockPaymentAndAccount);
    const voucherPicked = isCreate && setOff && !!values.setOffVoucher;
    const currencies = dropdowns.Currencies || [];
    const costCentres = COST_CENTRES.map((n) => ({ n, list: dropdowns[`CostCentre${n}`] || [] })).filter((c) => c.list.length > 0 && c.n <= 2); // Cost Centre 3 and 4 are hidden
    const rate = currencies.length === 0 ? 1 : num(values.conversionRate);
    const converted = round3(num(values.amount) * rate);
    const { allocated, unallocated } = summary(values.amount, rows, values.additionalAmount);
    const showBanner = (message) => setSubmitError(message);

    const [leaseParty, setLeaseParty] = useState('');
    const [leaseDetails, setLeaseDetail] = useState({
        leaseNo: [],
        property: [],
        unitNo: [],
        periodfrom: "",
        periodto: "",
        rent: "",
        TenantNo: "",
        TenantName: "",
        contactNo: "",
    });
    // ---------- loading ----------
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadState('loading');
            const ddPromise = dropdownCache
                ? Promise.resolve({ ok: true, dropdowns: dropdownCache })
                : api.getDropdowns(DROPDOWN_TYPES);
            const receiptPromise = isCreate ? Promise.resolve(null) : api.getReceiptByRdNo(rdNo);
            const [dd, receipt] = await Promise.all([ddPromise, receiptPromise]);
            if (cancelled) return;

            if (dd.ok) {
                dropdownCache = dd.dropdowns;
                setDropdowns(dd.dropdowns || {});
            } else {
                toast.error(dd.message);
            }

            if (isCreate) {
                setLoadState('ready');
                return;
            }
            if (!receipt.ok) {
                setLoadMessage(receipt.message);
                setLoadState(receipt.status === 404 ? 'notfound' : 'error');
                return;
            }

            const h = receipt.result?.header || {};
            setHeader(h);
            setRows((receipt.result?.lines || []).map((line) => ({ ...line, selected: true })));
            setValues({
                ...initialValues(),
                paymentType: h.paymentType || '',
                debitAccount: h.debitAccountCode ? { value: h.debitAccountCode, label: `${h.debitAccountName || ''} (${h.debitAccountCode})` } : null,
                amount: h.amount ?? '',
                currency: h.currency || DEFAULT_CURRENCY,
                conversionRate: h.conversionRate ?? 1,
                chequeNo: h.chequeNo || '',
                chequeDate: parseApiDate(h.chequeDate),
                drawnBank: h.drawnBank || '',
                voucherDate: parseApiDate(h.voucherDate),
                refNo: h.refNo || '',
                narration: h.commonNarration || '',
                cc1: h.costCenter1 || '', cc2: h.costCenter2 || '', cc3: h.costCenter3 || '', cc4: h.costCenter4 || '',
                additionalAccount: h.additionalAccountCode ? { value: h.additionalAccountCode, label: h.additionalAccountName || h.additionalAccountCode } : null,
                additionalAmount: h.additionalAmount ?? '',
            });
            setCustomerDetail({ customerNo: h.customerNo, customerName: h.customerName });
            setLoadState('ready');
            if (!h.isSetOff && h.paymentType) await loadDebitAccounts(h.paymentType, h.financialYear);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, rdNo]);

    useEffect(() => {
        getdrodown()
    }, []);

    const getdrodown = async () => {
        try {
            const params = new URLSearchParams();
            params.append('dropdownTypes', 'ReceiptLeaseNos,ReceiptPropertyNos');
            const customerRes = await apiService.getApiParamater('RECEIPT_DROPDOWN', `?${params.toString()}`);
            setProperty(customerRes?.data?.dropdowns?.PropertyNo);
            setLeaseNo(customerRes?.data?.dropdowns?.LeaseNo);
        } catch (error) {
            console.error('Error fetching data:', error?.customerRes?.data?.message);
        }
    }
    const loadDebitAccounts = async (paymentType, financialYear) => {
        setLoadingFlag('accounts', true);
        const res = await api.getDebitAccounts({ paymentType, financialYear });
        setLoadingFlag('accounts', false);
        if (!res.ok) {
            setDebitAccounts([]);
            if (res.status !== 204) toast.error(res.message);
            return;
        }
        const dabitparam = new URLSearchParams();
        dabitparam.append('Action', 'GetDrAccCodeByPropNo');
        dabitparam.append('PropertyNo', leaseDetails?.property?.value?.value);
        debugger
        const dabitRes = await apiService.getApiParamater('LEASE_NO_DATA', `?${dabitparam.toString()}`);
        let dabitData = dabitRes?.data?.result?.data
        const debitAccountCode = res.result?.find(pro => pro.accountName === dabitData?.DrAccName);
        debugger
        // No property default found: leave Debit Account empty (and selectable) instead of throwing.
        if (debitAccountCode) {
            setValue({ debitAccount: { value: debitAccountCode.accountCode, label: debitAccountCode.accountName || '' } });
        }
        setDebitAccounts(res.result || []);
    };

    const loadVouchers = async (customerNo, financialYear) => {
        if (!customerNo) return;
        setLoadingFlag('vouchers', true);
        const res = await api.getSetOffVouchers({ customerNo, financialYear });
        setLoadingFlag('vouchers', false);
        if (!res.ok) {
            setVouchers([]);
            if (res.status !== 204) toast.error(res.message);
            return;
        }
        setVouchers(res.result || []);
    };

    // Returns the fresh invoices (or null on failure). Latest request wins.
    const loadInvoices = async (customerNo, cc1, cc2) => {
        const ticket = ++invoiceRequest.current;
        setLoadingFlag('invoices', true);
        const res = await api.getOutstandingInvoices({ customerNo, costCenter1: cc1, costCenter2: cc2 });
        if (ticket !== invoiceRequest.current) return null;
        setLoadingFlag('invoices', false);
        if (!res.ok) {
            if (res.status !== 204) toast.error(res.message);
            return res.status === 204 ? [] : null;
        }
        return res.result || [];
    };

    const refreshInvoices = async (keepTicks) => {
        const customerNo = values.customer?.value;
        if (!customerNo) return;
        const fresh = await loadInvoices(customerNo, emptyToNull(values.cc1), emptyToNull(values.cc2));
        if (fresh === null) return;
        setRows((prev) => (keepTicks ? mergeKeepingTicks(prev, fresh) : fresh.map((r) => ({ ...r, selected: false, amount: 0 }))));
    };

    // ---------- create-mode handlers ----------
    const handleCustomerChange = async (option) => {
        const customerNo = option?.value || '';
        setValue({ customer: option || null, setOffVoucher: null, ...(setOff ? { amount: '', refNo: '', chequeNo: '', chequeDate: null } : {}) });
        setRows([]);
        setCustomerDetail(null);
        setErrors((prev) => ({ ...prev, customer: undefined }));
        if (!customerNo) return;

        setLoadingFlag('customer', true);
        const [detail, fresh] = await Promise.all([
            api.getCustomerDetail(customerNo),
            loadInvoices(customerNo, emptyToNull(values.cc1), emptyToNull(values.cc2)),
        ]);
        setLoadingFlag('customer', false);
        if (detail.ok) setCustomerDetail(detail.result); else toast.error(detail.message);
        if (fresh) setRows(fresh.map((r) => ({ ...r, selected: false, amount: 0 })));
        if (setOff) loadVouchers(customerNo, values.setOffFy);
    };

    // Tenant / Landlord / Freehold: resolve the customer for the selected property + unit, then fill it like a manual customer pick.
    const handlePartyChange = async (partyType) => {
        const propertyName = leaseDetails?.property?.value?.text;
        const unitNo = leaseDetails?.unitNo?.value?.value;
        if (!propertyName || !unitNo) {
            toast.error('Select Property and Unit No');
            return;
        }
        setLeaseParty(partyType);
        const res = await api.getCustomersByUnit({ partyType, propertyName, unitNo });
        const found = Array.isArray(res.result) ? res.result[0] : null;
        if (!found) {
            if (res.status === 204 || res.ok) toast.warning(`No ${partyType} found for this unit`);
            else toast.error(res.message);
            handleCustomerChange(null);
            return;
        }
        await handleCustomerChange({ value: found.customerNo, label: found.customerName });

        // Default the payment type to Bank Transfer (loads its debit accounts) unless one is already chosen.
        if (!values.paymentType) {
            const bankTransfer = toOptions(dropdowns.PaymentTypes)
                .find((o) => `${o.value}`.trim().toLowerCase() === DEFAULT_PAYMENT_TYPE.toLowerCase()
                    || `${o.label}`.trim().toLowerCase() === DEFAULT_PAYMENT_TYPE.toLowerCase());
            if (bankTransfer) handlePaymentTypeChange(bankTransfer);
        }
    };

    const handlePaymentTypeChange =(option) => {
        const paymentType = option?.value || '';
        if (isSetOff(paymentType) && !values.customer) {
            toast.error('Select Customer');
            return;
        }
        const leavingSetOff = setOff && !isSetOff(paymentType);
        const patch = { paymentType, debitAccount: null, setOffVoucher: null };
        if (leavingSetOff || isSetOff(paymentType)) {
            Object.assign(patch, { amount: '', refNo: '', chequeNo: '', chequeDate: null, currency: DEFAULT_CURRENCY, conversionRate: 1 });
            setRows((prev) => clearAllocations(prev));
        }
        if (!isChequeOrPdc(paymentType)) Object.assign(patch, { chequeNo: '', chequeDate: null, drawnBank: '' });
        setValue(patch);
        setErrors((prev) => ({ ...prev, paymentType: undefined, debitAccount: undefined }));
        setDebitAccounts([]);

        if (isSetOff(paymentType)) {
            loadVouchers(values.customer.value, values.setOffFy);
        } else if (paymentType) {
            loadDebitAccounts(paymentType, isEdit ? header?.financialYear : getSelectedFinancialYear());
        }
    };

    const handleSetOffFyChange = (option) => {
        setValue({ setOffFy: option?.value || '', setOffVoucher: null, amount: '', refNo: '', chequeNo: '', chequeDate: null });
        setRows((prev) => clearAllocations(prev));
        loadVouchers(values.customer?.value, option?.value);
    };

    const handleVoucherChange = (option) => {
        const v = option?.raw;
        if (!v) return;
        setValue({
            setOffVoucher: v,
            amount: v.balance,
            currency: v.currency || DEFAULT_CURRENCY,
            conversionRate: v.conversionRate || 1,
            refNo: v.refNo || '',
            chequeNo: v.chequeNo || '',
            chequeDate: parseApiDate(v.chequeDate),
            voucherDate: parseApiDate(v.voucherDate) || values.voucherDate,
        });
        setRows((prev) => clearAllocations(prev));
        setErrors((prev) => ({ ...prev, debitAccount: undefined, amount: undefined }));
    };

    const handleAmountChange = (raw) => {
        if (num(raw) < 0) {
            toast.error('Negative amount not allowed.');
            setValue({ amount: '0' });
            return;
        }
        setValue({ amount: raw });
        setRows(prev =>
            prev.map(row => ({
                ...row,
                selected: false
            }))
        );
    };
    const handleCurrencyChange = (option) => {
        const c = option?.raw;
        const nextRate = c?.rate ?? c?.conversionRate;
        setValue({ currency: option?.value || DEFAULT_CURRENCY, ...(nextRate ? { conversionRate: nextRate } : {}) });
    };

    const handleAdditionalAmountChange = (raw) => setValue({ additionalAmount: raw });

    const handleToggle = (index) => {
        const { rows: next, error } = toggleRow(rows, index, values.amount, values.additionalAmount);
        if (error) { toast.warn(error); return; }
        setRows(next);
        setDirty(true);
        setErrors((prev) => ({ ...prev, rows: undefined, allocation: undefined }));
    };

    const handleAmountCommit = (index, raw) => {
        const { rows: next, error } = setRowAmount(rows, index, raw, values.amount, values.additionalAmount);
        if (error) { toast.warn(error); return; }
        setRows(next);
        setDirty(true);
    };

    // ---------- submit ----------
    const buildValidationInput = () => ({ ...values, chequeNo: values.chequeNo.trim(), drawnBank: values.drawnBank.trim(), refNo: values.refNo.trim() });

    const handleSaveClick = () => {
        if (locked || saving) return;
        const found = validateReceipt(buildValidationInput(), {
            mode: isCreate ? 'create' : 'edit',
            rows,
            financialYearRange: isEdit ? getFinancialYearRange(header?.financialYear) : null,
        });
        setErrors(found);
        setSubmitError('');
        if (Object.keys(found).length > 0) return;
        setConfirm('save');
    };

    const costCentrePayload = () => ({
        costCenter1: emptyToNull(values.cc1), costCenter2: emptyToNull(values.cc2),
        costCenter3: emptyToNull(values.cc3), costCenter4: emptyToNull(values.cc4),
    });

    const buildCreateBody = () => {
        const body = {
            customerNo: values.customer.value,
            paymentType: values.paymentType,
            voucherDate: toApiDate(values.voucherDate),
            amount: round3(values.amount),
            refNo: emptyToNull(values.refNo),
            commonNarration: emptyToNull(values.narration),
            ...costCentrePayload(),
            additionalAccountCode: values.additionalAccount?.value || null,
            additionalAmount: round3(values.additionalAmount),
            allocations: rows.filter((r) => r.selected && num(r.amount) > 0).map((r) => ({ receiptNo: r.receiptNo, amount: round3(r.amount) })),
        };
        if (setOff) {
            body.setOffVoucherNo = values.setOffVoucher.voucherNo;
            body.setOffFinancialYear = values.setOffFy;
        } else {
            body.debitAccountCode = values.debitAccount.value;
            body.currency = values.currency || DEFAULT_CURRENCY;
            body.conversionRate = rate;
            body.chequeNo = showCheque ? emptyToNull(values.chequeNo) : null;
            body.chequeDate = showCheque ? toApiDate(values.chequeDate) : null;
            body.drawnBank = showCheque ? emptyToNull(values.drawnBank) : null;
        }
        return body;
    };

    const buildUpdateBody = () => ({
        paymentType: values.paymentType,
        debitAccountCode: setOff ? null : values.debitAccount?.value || null,
        voucherDate: toApiDate(values.voucherDate),
        chequeNo: showCheque ? emptyToNull(values.chequeNo) : null,
        chequeDate: showCheque ? toApiDate(values.chequeDate) : null,
        drawnBank: showCheque ? emptyToNull(values.drawnBank) : null,
        refNo: emptyToNull(values.refNo),
        commonNarration: emptyToNull(values.narration),
        ...costCentrePayload(),
    });

    const handleConfirmSave = async () => {
        if (saving) return;
        setSaving(true);
        setSubmitError('');
        const res = isCreate ? await api.createReceipt(buildCreateBody()) : await api.updateReceipt(rdNo, buildUpdateBody());
        setSaving(false);
        setConfirm(null);

        if (res.ok) {
            const voucherNo = res.result?.voucherNo || header?.voucherNo || '';
            toast.success(`Receipt ${voucherNo} ${isCreate ? 'saved' : 'updated'}`);
            onClose(true);
            return;
        }
        if (res.status === 404) {
            toast.error(res.message);
            onClose(false);
            return;
        }
        showBanner(res.message);
        if (res.status === 400 && isCreate && /balance|outstanding/i.test(res.message)) refreshInvoices(true);
    };

    const handleRemove = async () => {
        setSaving(true);
        const res = await api.deleteSetOffAllocation(rdNo);
        setSaving(false);
        setConfirm(null);
        if (res.ok) {
            toast.success('Allocation removed successfully.');
            onClose(true);
        } else {
            showBanner(res.message);
        }
    };
    const handleLeaseNoChange = async (selectedOption) => {
        const params = new URLSearchParams();
        params.append('Action', 'GetCustomerDetailsByLeaseNo');
        params.append('LeaseNo', selectedOption?.label);
        const res = await apiService.getApiParamater('LEASE_NO_DATA', `?${params.toString()}`);
        let data = res?.data?.result?.data
        const selectedProperty = property?.find(pro => pro?.value === data?.Property_No);
        if (!selectedProperty) {
            toast.warning("Property not Found")
            return
        }
        if (selectedProperty) {
            const Casparams = new URLSearchParams();
            Casparams.append('DropdownFor', 'LRUnitNosByPropertyId');
            Casparams.append('CascadValue', selectedProperty?.value);
            const casres = await apiService.getApiParamater('CASCAD_DROP', `?${Casparams.toString()}`);
            setUnitList(casres?.data?.items)
            const selectedUnit = casres?.data?.items?.find(pro => pro?.value === data?.Unit_No);
            setLeaseDetail({
                ...leaseDetails,
                leaseNo: selectedOption,
                property: { label: selectedProperty?.text, value: selectedProperty },
                unitNo: { label: selectedUnit?.value || data?.Unit_No, value: selectedUnit || data?.Unit_No },
            });
        }
    }
    // A new property / unit invalidates the party that was resolved for the old one.
    // Also clears the transaction and invoice sections, which were filled for the old customer. Cost centres are kept.
    const resetParty = () => {
        if (!isCreate) return;
        setLeaseParty('');
        invoiceRequest.current += 1;                 // drop any invoice fetch still in flight
        setLoadingFlag('invoices', false);
        setLoadingFlag('customer', false);
        setValues((prev) => ({ ...initialValues(), cc1: prev.cc1, cc2: prev.cc2, cc3: prev.cc3, cc4: prev.cc4 }));
        setRows([]);
        setCustomerDetail(null);
        setDebitAccounts([]);
        setVouchers([]);
        setErrors({});
        setSubmitError('');
        setDirty(false);
    };
    // Reset button: clears property, unit, party radio and the customer filled from them.
    const handleResetSelection = () => {
        resetParty();
        setUnitList([]);
        setLeaseDetail((prev) => ({ ...prev, property: [], unitNo: [], leaseNo: [] }));
    };
    const handlePropertyNoChange = async (selectedOption) => {
        resetParty();
        const params = new URLSearchParams();
        params.append('DropdownFor', 'LRUnitNosByPropertyId');
        params.append('CascadValue', selectedOption?.value?.value);
        const res = await apiService.getApiParamater('CASCAD_DROP', `?${params.toString()}`);
        setUnitList(res?.data?.items)

        setLeaseDetail({
            ...setLeaseDetail,
            property: selectedOption,
            leaseNo: [{ label: '', value: '' }],
            unitNo: [{ label: '', value: '' }],
        });
    }
    const handleUnitNoChange = async (selectedOption) => {
        resetParty();
        const selectedUnit = leaseNo?.find(pro => pro?.text === selectedOption?.value?.text);
        setLeaseDetail({
            ...leaseDetails,
            unitNo: selectedOption,
            leaseNo: { label: selectedUnit?.text, value: selectedUnit },
        });
    }
    const requestClose = () => (dirty && !locked ? setConfirm('discard') : onClose(false));

    // ---------- render helpers ----------
    const customerOptions = useMemo(() => toOptions(dropdowns.Customers), [dropdowns.Customers]);
    const paymentOptions = useMemo(() => toOptions(dropdowns.PaymentTypes)
        .filter((o) => isCreate || !isSetOff(o.value) || isSetOff(values.paymentType)), [dropdowns.PaymentTypes, isCreate, values.paymentType]);
    const fyOptions = useMemo(() => toOptions(dropdowns.FinancialYears), [dropdowns.FinancialYears]);
    const accountOptions = useMemo(() => debitAccounts.map((a) => ({
        value: a.accountCode, label: `${a.accountName} (${a.accountCode})`, raw: a,
    })), [debitAccounts]);
    const voucherOptions = useMemo(() => vouchers.map((v) => ({
        value: v.voucherNo,
        label: `${v.voucherNo}, ${moment(v.voucherDate).format('DD-MMM-YYYY')}, balance ${fmt3(v.balance)}`,
        raw: v,
    })), [vouchers]);
    const currentAccount = values.debitAccount && (accountOptions.find((o) => o.value === values.debitAccount.value) || values.debitAccount);
    const ledgerBalance = currentAccount?.raw?.balance || currentAccount?.balance || 0
    debugger
    const selectProps = { menuPortalTarget: document.body, menuPlacement: 'auto', menuShouldScrollIntoView: false, styles: selectStyles };
    const text = (key, maxLength, disabled) => (
        <input
            type="text"
            className="p-2 form-control"
            maxLength={maxLength}
            disabled={disabled}
            value={values[key]}
            onChange={(e) => setValue({ [key]: e.target.value })}
        />
    );

    const saveDisabled = saving || locked || (isCreate && (num(values.amount) <= 0 || unallocated !== 0));

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between border-box2 p-3" style={{ borderRadius: 8 }}>
                <h6 className="h6">
                    {isCreate ? 'New Lease Billwise Receipt' : `${isEdit ? 'Edit' : 'View'} Lease Billwise Receipt ${header?.voucherNo || ''}`}
                </h6>
                <div className="d-flex justify-content-end gap-1">
                    {isEdit && header?.canDelete && (
                        <button type="button" className="mainbtnRed" disabled={saving} onClick={() => setConfirm('remove')}>
                            <FaTrash className="center mt-1 mx-2" /><div>Remove Allocation</div>
                        </button>
                    )}
                    {loadState === 'ready' && !locked && (
                        <button type="button" className="mainbtn" disabled={saveDisabled} onClick={handleSaveClick}>
                            {saving ? <ClipLoader color="#ffffff" size={20} /> : <><FaSave className="center mt-1 mx-2" /><div>Save</div></>}
                        </button>
                    )}
                    <button type="button" className="mainbtnSecondary" disabled={saving} onClick={requestClose}>
                        <FaAngleLeft className="center mt-1 mx-2" /><div>{locked || loadState !== 'ready' ? 'Close' : 'Cancel'}</div>
                    </button>
                </div>
            </div>

            {loadState === 'loading' && <div className="text-center p-5"><ClipLoader color="#6c5ce7" size={36} /></div>}

            {(loadState === 'notfound' || loadState === 'error') && (
                <div className="border-box2 p-4 mt-3 text-center">
                    <p className={loadState === 'error' ? 'text-danger' : ''}>{loadMessage}</p>
                    <button type="button" className="mainbtn" onClick={() => onClose(false)}>Close</button>
                </div>
            )}

            {loadState === 'ready' && (
                <>
                    {submitError && <div className="alert alert-danger mt-3 mb-0">{submitError}</div>}
                    {isEdit && header && !header.canEdit && (
                        <div className="alert alert-warning mt-3 mb-0">{header.editBlockReason}</div>
                    )}
                    {isEdit && header?.canEdit && header.lockPaymentAndAccount && (
                        <div className="alert alert-info mt-3 mb-0">{LOCK_NOTE}</div>
                    )}
                    {['rows', 'allocation', 'additionalAccount', 'additionalAmount'].filter((k) => errors[k]).map((k) => (
                        <div key={k} className="alert alert-danger mt-3 mb-0">{errors[k]}</div>
                    ))}

                    <Section title="Cost Centers">
                        <div style={gridStyle}>

                            <div className="d-flex">
                                <label htmlFor="validationCustom01" className="form-label pt-1 start">Property *</label>
                                <WindowedSelect
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9
                                    })}

                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    menuShouldScrollIntoView={false}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                                            color: state.isFocused ? '#000 ' : '#000',
                                            ':hover': {
                                                backgroundColor: '#F5F5F5',
                                                color: '#000 ',
                                            },
                                        }),
                                    }}
                                    className="w-75 p-0"
                                    value={leaseDetails?.property}
                                    options={property?.map((sup) => ({ value: sup, label: sup?.text }))}
                                    onChange={(selectedOption) => handlePropertyNoChange(selectedOption)}
                                />
                            </div>
                            <div className="d-flex justify-content-between gap-3">
                                <div className="d-flex  w-75">
                                    <label htmlFor="validationCustom01" className="w-25 form-label pt-1 start">Unit No *</label>
                                    <WindowedSelect
                                        theme={(theme) => ({
                                            ...theme,
                                            borderRadius: 5,
                                            padding: 9
                                        })}

                                        menuPortalTarget={document.body}
                                        menuPlacement="auto"
                                        menuShouldScrollIntoView={false}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            option: (provided, state) => ({
                                                ...provided,
                                                backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                                                color: state.isFocused ? '#000 ' : '#000',
                                                ':hover': {
                                                    backgroundColor: '#F5F5F5',
                                                    color: '#000 ',
                                                },
                                            }),
                                        }}
                                        className="w-100 p-0"
                                        value={leaseDetails?.unitNo}
                                        options={unitList?.slice()?.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: 'base' }))?.map((sup) => ({ value: sup, label: sup?.value }))}
                                        onChange={(selectedOption) => handleUnitNoChange(selectedOption)}
                                    />
                                </div>
                            </div>
                            <div className="d-none">
                                <label htmlFor="validationCustom01" className="form-label pt-1 start">Lease No *</label>
                                <WindowedSelect
                                    className="w-75 p-0"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9
                                    })}

                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    menuShouldScrollIntoView={false}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? '#F5F5F5' : null,
                                            color: '#000',
                                            ':hover': {
                                                backgroundColor: '#F5F5F5',
                                                color: '#000',
                                            },
                                        }),
                                    }}
                                    value={leaseDetails.leaseNo}
                                    onChange={(selectedOption) => handleLeaseNoChange(selectedOption)}
                                    options={leaseNo?.map((rec) => ({ value: rec, label: rec?.text }))}
                                />
                            </div>
                            <div className="d-flex align-items-center flex-wrap" style={{ columnGap: 40, rowGap: 10, minWidth: 0 }}>
                                {['Tenant', 'Landlord', 'Freehold'].map((label) => (
                                    <div className="form-check m-0 d-flex align-items-center gap-2" key={label}>
                                        <input
                                            type="radio"
                                            className="form-check-input m-0"
                                            style={{ width: 22, height: 22, cursor: 'pointer' }}
                                            name="leaseParty"
                                            id={`leaseParty${label}`}
                                            value={label}
                                            checked={leaseParty === label}
                                            disabled={locked}
                                            onChange={() => handlePartyChange(label)}
                                        />
                                        <label className="form-check-label" style={{ fontSize: 15, cursor: 'pointer' }} htmlFor={`leaseParty${label}`}>{label}</label>
                                    </div>
                                ))}
                                <button type="button" className="mainbtnRed flex-shrink-0" disabled={locked} onClick={handleResetSelection}>
                                    <RiRefreshLine className="center mt-1 mx-2" /><div>Reset</div>
                                </button>
                            </div>
                            {costCentres.map(({ n, list }) => (
                                <Field key={n} label={`Cost Centre ${n}`}>
                                    <WindowedSelect
                                        {...selectProps}
                                        isClearable
                                        isDisabled={locked}
                                        placeholder="None"
                                        value={toOptions(list).find((o) => o.value === values[`cc${n}`]) || null}
                                        options={toOptions(list)}
                                        onChange={(o) => setValue({ [`cc${n}`]: o?.value || '' })}
                                    />
                                </Field>
                            ))}
                        </div>
                    </Section>
                    <Section title="Customer Details">
                        <div style={gridStyle}>
                            <Field label="Customer" error={errors.customer}>
                                {isCreate ? (
                                    <WindowedSelect
                                        {...selectProps}
                                        isClearable
                                        isDisabled={locked || !!values.customer || !!leaseParty}
                                        placeholder="--Select--"
                                        value={values.customer}
                                        options={customerOptions}
                                        onChange={handleCustomerChange}
                                    />
                                ) : (
                                    <input className="p-2 form-control" disabled value={`${header?.customerNo || ''} - ${header?.customerName || ''}`} />
                                )}
                            </Field>
                            <Field label="Cust No">
                                <div className="d-flex align-items-center gap-2">
                                    <input className="p-2 form-control" disabled value={customerDetail?.accountCode || customerDetail?.customerNo || ''} />
                                    {loading.customer && <ClipLoader size={16} color="#6c5ce7" />}
                                </div>
                            </Field>
                            <Field label="Address"><input className="p-2 form-control" disabled value={customerDetail?.address1 || ''} /></Field>
                            <Field label=""><input className="p-2 form-control" disabled value={customerDetail?.address2 || ''} /></Field>
                            <Field label="Contact No"><input className="p-2 form-control" disabled value={customerDetail?.contactNo || ''} /></Field>
                        </div>
                    </Section>

                    <Section title="Transaction Details">
                        <div style={gridStyle}>
                            <Field label="Payment Type" error={errors.paymentType}>
                                <WindowedSelect
                                    {...selectProps}
                                    isDisabled={paymentLocked}
                                    placeholder="--Select--"
                                    value={paymentOptions.find((o) => o.value === values.paymentType) || null}
                                    options={paymentOptions}
                                    onChange={handlePaymentTypeChange}
                                />
                            </Field>

                            {setOff ? (
                                <Field label="Receipt Voucher" error={errors.debitAccount}>
                                    {isCreate ? (
                                        <div className="d-flex gap-2">
                                            <div style={{ width: 130 }}>
                                                <WindowedSelect
                                                    {...selectProps}
                                                    isDisabled={locked}
                                                    value={fyOptions.find((o) => o.value === values.setOffFy) || null}
                                                    options={fyOptions}
                                                    onChange={handleSetOffFyChange}
                                                />
                                            </div>
                                            <div className="w-100">
                                                <WindowedSelect
                                                    {...selectProps}
                                                    isLoading={loading.vouchers}
                                                    isDisabled={locked}
                                                    placeholder="--Select--"
                                                    value={values.setOffVoucher ? voucherOptions.find((o) => o.value === values.setOffVoucher.voucherNo) || null : null}
                                                    options={voucherOptions}
                                                    onChange={handleVoucherChange}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <input className="p-2 form-control" disabled value={values.debitAccount?.label || ''} />
                                    )}
                                </Field>
                            ) : (
                                <Field label="Debit Account" error={errors.debitAccount}>
                                    <WindowedSelect
                                        {...selectProps}
                                        isLoading={loading.accounts}
                                        isDisabled={paymentLocked || (isCreate && !!values.debitAccount)}
                                        placeholder="--Select--"
                                        value={{ label: currentAccount?.accountName || currentAccount?.label, value: currentAccount?.value || currentAccount } || null}
                                        options={accountOptions}
                                        onChange={(o) => { setValue({ debitAccount: o }); setErrors((p) => ({ ...p, debitAccount: undefined })); }}
                                    />
                                    {values.debitAccount && ledgerBalance !== undefined && (
                                        <div className="small text-muted mt-1">Ledger balance: {fmt3(ledgerBalance)}</div>
                                    )}
                                </Field>
                            )}

                            <Field label="Amount" error={errors.amount}>
                                <input
                                    type="number"
                                    step="0.001"
                                    className="p-2 form-control text-end"
                                    disabled={!isCreate || locked || voucherPicked}
                                    value={values.amount}
                                    onChange={(e) => handleAmountChange(e.target.value)}
                                    onBlur={() => values.amount !== '' && setValues((p) => ({ ...p, amount: fmt3(p.amount) }))}
                                />
                            </Field>

                            {!setOff && (
                                <>
                                    <Field label="Currency">
                                        {currencies.length === 0 ? (
                                            <input className="p-2 form-control" disabled value={DEFAULT_CURRENCY} />
                                        ) : (
                                            <WindowedSelect
                                                {...selectProps}
                                                isDisabled={!isCreate || locked}
                                                value={toOptions(currencies).find((o) => o.value === values.currency) || null}
                                                options={toOptions(currencies)}
                                                onChange={handleCurrencyChange}
                                            />
                                        )}
                                    </Field>
                                    <Field label="Conversion Rate" error={errors.conversionRate}>
                                        <input
                                            type="number"
                                            step="0.0001"
                                            className="p-2 form-control text-end"
                                            disabled={!isCreate || locked || currencies.length === 0}
                                            value={currencies.length === 0 ? 1 : values.conversionRate}
                                            onChange={(e) => setValue({ conversionRate: e.target.value })}
                                        />
                                    </Field>
                                    <Field label="Converted Amount">
                                        <input className="p-2 form-control text-end" disabled value={fmt3(converted)} />
                                    </Field>
                                </>
                            )}

                            {(showCheque || (setOff && values.chequeNo)) && (
                                <>
                                    <Field label="Cheque No" error={errors.chequeNo}>
                                        {text('chequeNo', MAX.chequeNo, locked || voucherPicked)}
                                    </Field>
                                    <Field label="Cheque Date" error={errors.chequeDate}>
                                        <DatePicker
                                            selected={values.chequeDate}
                                            onChange={(d) => setValue({ chequeDate: d })}
                                            dateFormat="dd-MMM-yyyy"
                                            className="p-2 form-control w-100"
                                            disabled={locked || voucherPicked}
                                        />
                                    </Field>
                                </>
                            )}
                            {showCheque && (
                                <Field label="Drawn Bank" error={errors.drawnBank}>{text('drawnBank', MAX.drawnBank, locked)}</Field>
                            )}

                            <Field label="Voucher Date" error={errors.voucherDate}>
                                <DatePicker
                                    selected={values.voucherDate}
                                    onChange={(d) => setValue({ voucherDate: d })}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100"
                                    disabled={locked}
                                />
                            </Field>
                            <Field label="Ref No" error={errors.refNo}>{text('refNo', MAX.refNo, locked || voucherPicked)}</Field>
                            <Field label="Common Narration" error={errors.narration}>
                                <textarea
                                    className="p-2 form-control"
                                    maxLength={MAX.narration}
                                    disabled={locked}
                                    value={values.narration}
                                    onChange={(e) => setValue({ narration: e.target.value })}
                                />
                            </Field>

                        </div>
                    </Section>


                    <Section title="Invoice Details">
                        {/* {isCreate && (
                            <div className="mb-2">
                                <button
                                    type="button"
                                    className="mainbtnGry"
                                    disabled={!values.customer || loading.invoices || locked}
                                    onClick={() => refreshInvoices(true)}
                                >
                                    Filter by Cost Centre 1 / 2
                                </button>
                            </div>
                        )} */}
                        <AllocationGrid
                            rows={rows}
                            readOnly={!isCreate}
                            loading={loading.invoices}
                            onToggle={handleToggle}
                            onAmountCommit={handleAmountCommit}
                        />
                    </Section>
                    <Section title="FX / Additional Difference">
                        <div style={gridStyle}>
                            <Field label="Additional Account" error={errors.additionalAccount}>
                                <WindowedSelect
                                    {...selectProps}
                                    isClearable
                                    isDisabled={!isCreate || locked}
                                    placeholder="--Select--"
                                    value={values.additionalAccount}
                                    options={toOptions(dropdowns.AdditionalAccounts)}
                                    onChange={(o) => setValue({ additionalAccount: o || null })}
                                />
                            </Field>
                            <Field label="Additional Amount" error={errors.additionalAmount}>
                                <input
                                    type="number"
                                    step="0.001"
                                    className="p-2 form-control text-end"
                                    disabled={!isCreate || locked}
                                    value={values.additionalAmount}
                                    onChange={(e) => handleAdditionalAmountChange(e.target.value)}
                                />
                            </Field>
                        </div>
                    </Section>
                    <div className="border-box2 p-3 mt-3 d-flex gap-5 justify-content-end">
                        <div><span className="text-muted">Amount: </span><strong>{fmt3(values.amount)}</strong></div>
                        <div><span className="text-muted">Allocated: </span><strong>{fmt3(allocated)}</strong></div>
                        <div>
                            <span className="text-muted">Unallocated: </span>
                            <strong style={{ color: !isCreate || unallocated === 0 ? 'green' : 'red' }}>{fmt3(unallocated)}</strong>
                        </div>
                    </div>
                </>
            )}

            <Modal show={confirm === 'save'} onHide={() => setConfirm(null)} backdrop="static" size="sm">
                <Modal.Header closeButton><Modal.Title>Confirm</Modal.Title></Modal.Header>
                <Modal.Body>{isCreate ? 'Save receipt?' : `Update receipt ${header?.voucherNo || ''}?`}</Modal.Body>
                <Modal.Footer>
                    <button type="button" className="mainbtnGry" disabled={saving} onClick={() => setConfirm(null)}>No</button>
                    <button type="button" className="mainbtnRed center" disabled={saving} onClick={handleConfirmSave}>
                        {saving ? <ClipLoader color="#ffffff" size={18} /> : 'Yes'}
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={confirm === 'remove'} onHide={() => setConfirm(null)} backdrop="static" size="sm">
                <Modal.Header closeButton><Modal.Title>Remove Allocation</Modal.Title></Modal.Header>
                <Modal.Body>Do you want to remove this allocation?</Modal.Body>
                <Modal.Footer>
                    <button type="button" className="mainbtnGry" disabled={saving} onClick={() => setConfirm(null)}>No</button>
                    <button type="button" className="mainbtnRed center" disabled={saving} onClick={handleRemove}>
                        {saving ? <ClipLoader color="#ffffff" size={18} /> : 'Yes'}
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={confirm === 'discard'} onHide={() => setConfirm(null)} backdrop="static" size="sm">
                <Modal.Header closeButton><Modal.Title>Discard changes?</Modal.Title></Modal.Header>
                <Modal.Body>You have unsaved changes. Are you sure you want to discard them?</Modal.Body>
                <Modal.Footer>
                    <button type="button" className="mainbtnGry" onClick={() => setConfirm(null)}>No</button>
                    <button type="button" className="mainbtnRed" onClick={() => onClose(false)}>Yes, discard</button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default LeaseGeneralInvoiceReceiptForm;
